import React, { FC } from 'react';
import produce from 'immer';
import { get, merge } from 'lodash';
import { useTranslation } from 'react-i18next';

import FormDrawer from '~/components/FormDrawer';
import { ApplicationPayload } from '~/types/ApplicationPayload';

import buildNamePath from '~/utils/buildNamePath';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { getBeneficialOwnersFormItemsNamePath } from '~/pages/ksa/application/shareholders/components/beneficialOwners/BeneficialOwnerForm';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import { hasAnyDeclinedField } from '~/pages/ksa/application/shareholders/components/beneficialOwners/BeneficialOwnerTable';
import BeneficialOwnerReviewForm, {
  formName,
  getBeneficialOwnersReviewFormItemsNamePath,
} from './BeneficialOwnerReviewForm';

interface Props extends Partial<FormItemInjectedProps<KsaShareholders>> {
  uid: string;
  onClose: () => void;
}

const BeneficialOwnerReviewEdit: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const { t } = useTranslation('ksaReview');
  const reviewShareholdersFormState = { reviewShareholders: value };
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();

  const currentIndex =
    (payload?.shareholders as KsaShareholders)?.beneficialOwners?.findIndex(
      (item) => item?.uid === uid
    ) || 0;

  const beneficialOwnerData = get(payload, getBeneficialOwnersFormItemsNamePath(currentIndex));

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(reviewShareholdersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.reviewShareholders);
    onClose();
  };

  const withContinueButton = !isEditButtonDisabled(
    applicationQuery.data,
    hasAnyDeclinedField(applicationQuery?.data)
  );

  return (
    <FormDrawer
      id="beneficialOwnerReviewDrawer"
      visible={!!uid}
      onClose={onClose}
      title={t('Edit beneficial owner')}
      formName={formName}
      withContinueButton={withContinueButton}
    >
      <BeneficialOwnerReviewForm
        onFinish={onFinish}
        n={buildNamePath(getBeneficialOwnersReviewFormItemsNamePath(currentIndex))}
        initialValues={reviewShareholdersFormState}
        beneficialOwnerData={beneficialOwnerData}
      />
    </FormDrawer>
  );
};

export default BeneficialOwnerReviewEdit;
